import { formatGaslessRelayError as formatSharedGaslessRelayError } from "./gaslessRelayError";
import { MARKET_CONTRACT_SPECS_ABI } from "../../contracts/useMarketContractSpecs";
import { logger } from "../../../utils/logger";
import { ethers, BigNumber } from "ethers";
import type { ChainConfig } from "../../../config/chains";
import {
  GetOrders200Response,
  InstrumentTypeResponse,
  OrderStatusResponse,
} from "../../../codegen-api";
import { getCurrentChainConfig, getUsdcDecimals } from "../../../config/chains";
import { formatParsedContractError } from "../../../utils/errors.decode";
import { toOrderTransactionError } from "./orderTransactionError";
import { readProvider } from "../../../utils/wallet/activeSigner";
import {
  PERP_MARGIN_MODE,
  PerpMarginMode,
  toPerpMarginMode,
} from "../../../utils/margin";
import { PositionWithIdentity } from "../../../utils/positionIdentity";
import { IPerpOrderParams } from "./usePerpOrder.types";
import { toTokenUnits } from "../../../utils/decimalString";

// PerpEngine ABI - user-callable functions (no adapter auth needed)
export const PERP_ENGINE_ABI = [
  "function openPosition(address trader, address market, uint128 size, uint128 price, bool isLong, uint8 mode, uint16 leverage, uint8 orderType, uint256 acceptablePrice) external returns (uint48)",
  "function placeReduceOnlyOrder(address trader, address market, uint128 size, uint128 price, bool isLong, uint8 mode, uint8 orderType, uint256 acceptablePrice) external returns (uint48)",
  "function closePosition(address trader, address market, bool isLong, uint8 mode, uint256 sizeInTokensToClose, uint256 acceptablePrice) external returns (uint48)",
];

export const PERP_ORDER_HANDLER_ABI = [
  "function cancelOrder(uint48 orderId, address account) external",
  "function cancelOrderBatch(uint48[] orderIds, address account) external",
];

const PERP_ERROR_INTERFACE = new ethers.utils.Interface([
  "error InsufficientBalance(uint256 required, uint256 available)",
  "error DepositLockActive(uint256 unlockBlock)",
]);

export const PERP_OPEN_GAS_FLOOR = ethers.BigNumber.from(8000000);
export const PERP_CLOSE_GAS_FLOOR = ethers.BigNumber.from(8000000);

const extractErrorData = (error: any): string | undefined => {
  if (!error || typeof error !== "object") return undefined;
  if (typeof error.data === "string") return error.data;
  if (typeof error.error?.data === "string") return error.error.data;
  if (typeof error.error?.data?.data === "string") return error.error.data.data;
  return extractErrorData(error.error);
};

const formatPerpRevert = (error: any): string | undefined => {
  const data = extractErrorData(error);
  if (!data) return undefined;

  try {
    const parsed = PERP_ERROR_INTERFACE.parseError(data);
    if (parsed.name === "InsufficientBalance") {
      const required = ethers.utils.formatUnits(
        parsed.args.required,
        getUsdcDecimals()
      );
      const available = ethers.utils.formatUnits(
        parsed.args.available,
        getUsdcDecimals()
      );
      return `Insufficient margin: requires ${required} USDC, available ${available} USDC. Reduce order size, increase leverage, or deposit more collateral.`;
    }
    if (parsed.name === "DepositLockActive") {
      return `Deposit is still locked until block ${parsed.args.unlockBlock.toString()}. Try again after the next block.`;
    }
  } catch {
    return undefined;
  }

  return undefined;
};

export const toPerpTransactionError = (error: any, fallback: string) =>
  // Our own domain errors pass through untouched (see isOwnOrderError); a
  // contract revert is decoded by the shared formatter first, then by the
  // perp-specific one that knows the USDC decimals.
  toOrderTransactionError(error, fallback, formatPerpRevert);

// Decode a gasless-relay error into a user-friendly message. The shared
// formatter owns the name-matching and the opaque-revert wording; perp supplies
// the returndata decoder so a `0x…` payload still recovers e.g. the
// DepositLockActive unlock block. Spot uses the same formatter (without an ABI
// decoder), so both paths now say the same thing for the same failure.
export const formatGaslessRelayError = (
  raw: string | undefined,
  fallback: string,
  reverted?: boolean
): string =>
  formatSharedGaslessRelayError(raw, fallback, {
    reverted,
    // The perp-specific decoder first (it knows the USDC decimals), then the
    // shared signature table — without it a relayed InsufficientLiquidity or
    // StalePrice fell through to the generic "rejected on-chain" sentence.
    decodeRevertData: (hex) =>
      formatPerpRevert({ data: hex }) ?? formatParsedContractError(hex),
  });

// Re-run openPosition as a read-only eth_call against the app's configured RPC to
// recover the revert returndata. The connected wallet's RPC often returns only the
// request calldata on a failed simulation (so the UI showed the bare openPosition
// selector 0x51359a95); the app RPC returns the actual custom-error bytes (e.g.
// InsufficientMargin / 0xcf479181), which formatContractError can then decode.
export const probeOpenPositionRevert = async (
  perpEngine: string,
  args: any[]
): Promise<any | undefined> => {
  const rpcUrls = getCurrentChainConfig().rpcUrls?.filter(Boolean) ?? [];
  if (rpcUrls.length === 0) return undefined;
  const iface = new ethers.utils.Interface(PERP_ENGINE_ABI);
  const data = iface.encodeFunctionData("openPosition", args);
  // Try each configured RPC until one actually responds with the revert (the
  // primary may be down, or may strip the returndata). from = trader (args[0]);
  // openPosition is gated by onlyTraderOrOperator.
  for (const rpcUrl of rpcUrls) {
    try {
      const readProvider = new ethers.providers.JsonRpcProvider(rpcUrl);
      await readProvider.call({ to: perpEngine, data, from: args[0] });
      return undefined; // didn't revert on this RPC — let the original error stand
    } catch (probeErr: any) {
      // A revert (CALL_EXCEPTION) carries the custom-error returndata we want.
      // A connectivity failure does NOT — fall through to the next RPC.
      if (extractErrorData(probeErr)) return probeErr;
    }
  }
  return undefined;
};

const OPEN_ORDER_STATUSES = new Set<string>([
  OrderStatusResponse.Opened,
  OrderStatusResponse.Partial,
  "open",
  "partially_filled",
]);

export const isOpenOrder = (order: GetOrders200Response) => {
  const status = String(order.order_status || "").toLowerCase();
  return !status || OPEN_ORDER_STATUSES.has(status);
};

export const isPerpOrder = (order: GetOrders200Response) =>
  order.instrument_type === InstrumentTypeResponse.Perpetual ||
  String(order.instrument_name || "").toUpperCase().endsWith("-PERP");

export const sameMarket = (
  order: GetOrders200Response,
  market: string,
  instrumentName?: string
) => {
  const normalizedMarket = market.toLowerCase();
  const normalizedOrderMarket = String(order.instrument_id || "").toLowerCase();
  const normalizedInstrumentName = String(instrumentName || "").toLowerCase();
  const normalizedOrderInstrumentName = String(order.instrument_name || "").toLowerCase();

  return (
    normalizedOrderMarket === normalizedMarket ||
    (!!normalizedInstrumentName &&
      normalizedOrderInstrumentName === normalizedInstrumentName)
  );
};

export const numericOrderId = (orderId?: string) =>
  orderId && /^\d+$/.test(orderId) ? BigNumber.from(orderId) : undefined;

/**
 * A raw (1e18) limit price aligned to the market's on-chain `priceStep`: floored
 * for a buy (never pays more than entered), ceiled for a sell (never sells for
 * less). An off-step price otherwise reverts PriceNotOnStep after signing.
 */
export const alignRawPriceToStep = (
  rawPrice: BigNumber,
  priceStep: BigNumber | undefined,
  isLong: boolean
): BigNumber => {
  if (!priceStep || priceStep.lte(0) || rawPrice.lte(0)) return rawPrice;
  const remainder = rawPrice.mod(priceStep);
  if (remainder.eq(0)) return rawPrice;
  const floored = rawPrice.sub(remainder);
  return isLong ? floored : floored.add(priceStep);
};

/**
 * Display text for a size or price headed on-chain, as 1e18 units. Plain decimals
 * only: `parseUnits` throws on exponent text ("5e-7") and on more than 18 places.
 */
export const parsePerpUnits = (value: string | undefined, label: string): BigNumber => {
  const units = toTokenUnits(value ?? "0", 18);
  if (!units) {
    throw new Error(`Invalid ${label} "${value}". Enter a plain number.`);
  }
  return units;
};

// Floor the requested size to the market's on-chain amountStep. Extracted verbatim
// from placePerpOrder so DataStore.setMarketContractSpecs (size % amountStep == 0)
// can never revert SizeNotOnStep after the user signs. Refuses to submit when the
// live step cannot be verified rather than falling back to a raw size.
export const floorPerpSizeToStep = async (
  config: ChainConfig,
  params: IPerpOrderParams,
  rawSizeRaw: BigNumber,
  // Kept for call-site compatibility; deliberately UNUSED for the read below.
  // See the `readProvider` note.
  _walletProvider?: ethers.providers.Web3Provider
): Promise<{ size: BigNumber; priceStep: BigNumber | undefined }> => {
  let rawSize = rawSizeRaw;
  const dataStoreAddress = config.contracts.perpetualDex?.dataStore;
  logger.debug("[PerpOrder][step] dataStoreAddress:", dataStoreAddress, "market:", params.market);
  if (!dataStoreAddress || dataStoreAddress === ethers.constants.AddressZero) {
    throw new Error(
      "DataStore address not configured for this chain. Cannot verify order size lot alignment. Refusing to submit.",
    );
  }

  // Read the lot size over the chain's CONFIGURED RPC, not the user's wallet.
  //
  // `getMarketContractSpecs` is a view call: it needs a node on the right chain,
  // and nothing else. Routing it through the wallet made it fail whenever the
  // wallet sat on another network — the DataStore address holds no contract
  // there, so ethers threw a call revert and this function reported "Cannot read
  // market lot size", blocking an order for a reason that had nothing to do with
  // the order. The wallet is still required to SIGN, and that is guarded
  // separately by `getSignerForChain`.
  const dataStore = new ethers.Contract(
    dataStoreAddress,
    MARKET_CONTRACT_SPECS_ABI,
    readProvider(config),
  );
  let specs: any;
  let lastErr: any;
  for (let attempt = 1; attempt <= 2; attempt++) {
    try {
      specs = await dataStore.getMarketContractSpecs(params.market);
      break;
    } catch (e: any) {
      lastErr = e;
      console.warn(`[PerpOrder][step] getMarketContractSpecs attempt ${attempt}/2 failed:`, e?.message);
    }
  }
  if (!specs) {
    throw new Error(
      `Cannot read market lot size (DataStore call failed: ${lastErr?.message ?? "unknown"}). Refusing to submit — try again in a moment.`,
    );
  }
  const priceStep: BigNumber | undefined = specs.priceStep ?? specs[0];
  const amountStep: BigNumber = specs.amountStep ?? specs[1];
  const minOrderSize: BigNumber = specs.minOrderSize ?? specs[2];
  logger.debug("[PerpOrder][step] live amountStep:", amountStep?.toString(), "minOrderSize:", minOrderSize?.toString());

  if (!amountStep || amountStep.lte(0)) {
    throw new Error(
      "Market lot size (amountStep) is zero — market may not be registered. Refusing to submit.",
    );
  }

  const flooredSize = rawSizeRaw.div(amountStep).mul(amountStep);
  if (!flooredSize.eq(rawSizeRaw)) {
    logger.debug("[PerpOrder][step] FLOORED size to amountStep", {
      requested: ethers.utils.formatUnits(rawSizeRaw, 18),
      requestedWei: rawSizeRaw.toString(),
      floored: ethers.utils.formatUnits(flooredSize, 18),
      flooredWei: flooredSize.toString(),
      amountStep: ethers.utils.formatUnits(amountStep, 18),
      amountStepWei: amountStep.toString(),
    });
  }
  if (flooredSize.eq(0)) {
    throw new Error(
      `Order size ${params.size} is smaller than the market's lot size (${ethers.utils.formatUnits(amountStep, 18)}). Increase your size.`,
    );
  }
  if (minOrderSize && minOrderSize.gt(0) && flooredSize.lt(minOrderSize)) {
    throw new Error(
      `Order size ${ethers.utils.formatUnits(flooredSize, 18)} is below market minimum ${ethers.utils.formatUnits(minOrderSize, 18)}. Increase your size.`,
    );
  }
  rawSize = flooredSize;

  // Final defensive assertion — verify what we're about to submit IS aligned.
  // If this trips, somebody mutated rawSize between here and submission, OR
  // the floor math is wrong. Either way: don't submit, fail loudly.
  if (!rawSize.mod(amountStep).eq(0)) {
    throw new Error(
      `INTERNAL: post-floor size ${rawSize.toString()} is not a multiple of amountStep ${amountStep.toString()}. Bug — refusing to submit.`,
    );
  }
  logger.debug("[PerpOrder][step] FINAL aligned size:", rawSize.toString(), "(mod check OK)");
  return { size: rawSize, priceStep };
};

/**
 * Resolve the on-chain `marginMode` for a position we are about to act on.
 *
 * The mode selects WHICH leg the router touches — a trader can hold a cross and
 * an isolated position on the same market and side — so there is no safe
 * default. The close path used to fall through to CROSS whenever `margin_type`
 * was absent, silently closing a position the trader had not picked.
 *
 * Two DECLARED sources are consulted, never inferred:
 *   1. `margin_type` — the direct answer when present.
 *   2. `liquidation_price_type` — an `"isolated" | "cross_estimate"` enum on the
 *      same payload that states the same fact. `margin_type` is optional on the
 *      generated type, and only one deployment's API has been observed
 *      first-hand, so relying on it alone risked blocking every close on a
 *      backend that happens to omit it.
 *
 * If neither is present the mode is genuinely unknown and we refuse: an error
 * the trader can act on beats a trade against the wrong leg.
 */
export const resolvePerpMarginMode = (
  position: Pick<
    PositionWithIdentity,
    "margin_type" | "instrument_name" | "liquidation_price_type"
  >
): PerpMarginMode => {
  const declared = toPerpMarginMode(position?.margin_type);
  if (declared !== undefined) return declared;

  const liquidationBasis = String(
    position?.liquidation_price_type ?? ""
  ).toLowerCase();
  if (liquidationBasis === "isolated") return PERP_MARGIN_MODE.ISOLATED;
  if (liquidationBasis === "cross_estimate") return PERP_MARGIN_MODE.CROSS;

  throw new Error(
    `Could not determine the margin mode for ${
      position?.instrument_name || "this position"
    }. Refresh your positions and try again.`
  );
};
